/*
 * Only being used by visibility rules and descriptions as far as I can tell
 */

import React, { useEffect, useState } from 'react';
import Animation from '../../components/Animation';
import styled from 'styled-components/macro';
import {
  Collapse,
  InputBase as MuiInput,
  MenuItem as MuiMenuItem,
  Tooltip,
  TextField,
  Typography
} from '@material-ui/core';
import cloneDeep from 'lodash/cloneDeep';
import dotProp from 'dot-prop-immutable';
import generateId from '../../utils/generateId';
import {
  AddCircleOutline as AddCircleIcon,
  RemoveCircleOutline as RemoveCircleIcon,
  Remove as RemoveIcon,
  Add as AddIcon
} from '@material-ui/icons';
import getUTCNoonForDate from '../../utils/getUTCNoonForDate';
import { Select } from '../form-builder/FormBuilderFieldForm';
import TextString from '../../components/Text';
import useTranslations from '../../hooks/useTranslations';
import { InlineSelect } from './InlineSelect';
import { SmallIconButton } from './SmallIconButton';
import { unwrapPercents, wrapPercents } from './wrapPercents';
import {
  defaultComparisonMap,
  negatedDefaultComparisonMap
} from './defaultComparisonMap';
import ErrorIcon from '@material-ui/icons/ErrorTwoTone';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Input from '@material-ui/core/Input';

import OTSelect from '../../components/engine/SelectEngine';
import InputBase from '../../components/form/InputBase';
import moment from 'moment';
import { DatePicker as MuiDatePicker } from '@material-ui/pickers';
import MuiIconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';
import SerializationCopy from '../../utils/SerializationCopy';
import { NumberInput } from '../form-builder/view/RemindersStyledInputs';
import DateComparisonEngine from '../../components/conditions-query-builder/engine/DateComparisonEngine';

const DatePicker = styled(MuiDatePicker)`
  button[class*='MuiButtonBase'] {
    padding: 8px;
  }

  svg {
    width: 0.7em;
    height: 0.7em;
  }
`;

const IconButton = styled(MuiIconButton)`
  && {
    padding: 0 !important;
  }
`;

const ErrorMessage = styled.em`
  && {
    color: #dc004e;
  }

  svg {
    position: relative;
    top: 6px;
    font-size: 1.5em;
    margin-right: 10px;
  }
`;

const Wrap = styled.span`
  && {
    // width: calc(100% - 64px);
    display: inline-block;
    max-width: 400px;
    white-space: normal;
  }
`;

const StyledOption = styled(TextString)`
  [class*='MuiSelect'] && {
    font-weight: 700 !important;
  }
`;

const BlankOption = styled(MuiMenuItem)`
  font-style: italic;
  p {
    margin: 0 !important;
  }
`;

const parseDate = date => {
  let parsedDate = new Date(date);

  if (!(parsedDate instanceof Date && !isNaN(parsedDate))) {
    parsedDate = new Date(parseInt(date));
  }
  return parsedDate;
};

const AndOr = ({ andOr, update }) => (
  <InlineSelect
    value={andOr}
    onChange={e => update({ andOr: e.target.value })}
    input={<MuiInput />}
    IconComponent={() => null}
    SelectDisplayProps={{ style: { padding: 8, fontWeight: 700 } }}
    data-testid="logicbuilder-select-and-or"
  >
    <MuiMenuItem
      data-testid="logicbuilder-select-and-or-option-and"
      value="and"
    >
      and
    </MuiMenuItem>
    <MuiMenuItem data-testid="logicbuilder-select-and-or-option-or" value="or">
      or
    </MuiMenuItem>
  </InlineSelect>
);

const AndNotSpan = styled.span`
  margin: -4px;
`;

const AndNot = () => (
  <AndNotSpan>
    <TextString content="logic.and_is_not" />
  </AndNotSpan>
);

const IsAint = ({ negated, negate }) => (
  <InlineSelect
    value={negated || false}
    onChange={e => negate(e.target.value)}
    input={<MuiInput />}
    IconComponent={() => null}
    SelectDisplayProps={{ style: { padding: 8, fontWeight: 700 } }}
    data-testid="logicbuilder-select-is-isnot"
  >
    <MuiMenuItem
      data-testid="logicbuilder-select-is-isnot-option-is"
      value={false}
    >
      <StyledOption content="logic.isaint.is" />
    </MuiMenuItem>
    <MuiMenuItem
      data-testid="logicbuilder-select-is-isnot-option-isnot"
      value={true}
    >
      <StyledOption content="logic.exact.not" />
    </MuiMenuItem>
  </InlineSelect>
);

const ComparisonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: stretch;
  margin: 12px 0 0;

  [data-testid] {
    transition: opacity 0.15s linear;
    opacity: ${({ removeHovered }) => (removeHovered ? 0.33 : 1)};
  }

  [data-testid='logicbuilder-remove-comparison'],
  [data-testid='logicbuilder-add-comparison'] {
    opacity: 1;
  }
`;

const FlexSpan = styled.span`
  display: inline-flex;
  flex: ${({ flex }) => flex || 1};
  margin: 0 ${({ noMargin }) => (noMargin ? '' : '4px')};
  align-items: center;
  ${({ centerContent = true }) => centerContent && 'justify-content: center;'}
`;

const LightSpan = styled.span`
  opacity: 0.4;
  margin-left: 0.5ch;
`;

export const ComparisonBuilder = ({
  comparisons,
  compareType,
  input,
  j,
  update,
  andOr,
  remove,
  options,
  variant = 'drawer'
}) => {
  const [removeHovered, setRemovedHovered] = React.useState(false);
  const [deleteMe, setDeleteMe] = React.useState(false);
  const t = useTranslations();
  let intervalUnit, intervalCount, op, value;

  const getNewComparator = (compareType, negated) => {
    if (compareType === 'multiple_choice') {
      if (negated) {
        return 'NOT ILIKE';
      } else {
        return 'ILIKE';
      }
    } else if (compareType === 'exact') {
      if (negated) {
        return '<>';
      } else {
        return '=';
      }
    }
  };

  const negate = val => {
    let newValue = !!val;
    let comparator = getNewComparator(compareType, newValue);
    let newComparisons = [];

    comparisons.forEach((c, i) => {
      let newComparison = cloneDeep(c);
      newComparison.negated = newValue;
      newComparison.op = comparator;
      newComparisons[i] = newComparison;
    });

    update({
      andOr: 'or',
      comparisons: newComparisons
    });
  };

  const updateDateRule = obj => {
    const { deleteKeys = [] } = obj;
    let newComparison = cloneDeep(comparisons[j]);
    newComparison = { ...newComparison, ...obj };
    deleteKeys.forEach(key => delete newComparison[key]);
    update({ comparisons: dotProp.set(comparisons, j, newComparison) });
  };

  if (compareType === 'date' && comparisons.length && comparisons[j]) {
    ({ intervalUnit, intervalCount, op, value } = comparisons[j]);
  }

  return (
    <Collapse
      appear
      in={!deleteMe}
      onExited={() => {
        update({
          comparisons: dotProp.delete(comparisons, j)
        });
      }}
    >
      <ComparisonWrapper
        data-testid={`logicbuilder-${compareType}-comparison-${input}-${j}`}
        removeHovered={removeHovered}
      >
        <FlexSpan
          flex={variant === 'drawer' ? 1 : 0.3}
          data-testid="comparison-is-or-and"
        >
          {['multiple_choice'].indexOf(compareType) !== -1 ? (
            j === 0 ? (
              <IsAint negate={negate} negated={comparisons[j].negated} />
            ) : j > 0 && comparisons[j].negated ? (
              <AndNot />
            ) : (
              <AndOr andOr={andOr} update={update} />
            )
          ) : j === 0 ? (
            t(`logic.${compareType}.is`)
          ) : (
            <AndOr andOr={andOr} update={update} />
          )}
        </FlexSpan>
        {compareType === 'number' && (
          <NumberComparison {...{ comparisons, j, update, variant }} />
        )}
        {compareType === 'date' && (
          <DateComparisonEngine
            intervalUnit={intervalUnit}
            intervalCount={intervalCount}
            op={op}
            value={value}
            updateDateRule={updateDateRule}
            variant={variant}
          />
        )}
        {compareType === 'exact' && (
          <DropdownComparison
            {...{ comparisons, j, update, options, variant }}
          />
        )}
        {compareType === 'yes_no' && (
          <ExactComparison {...{ comparisons, j, update, options, variant }} />
        )}
        {compareType === 'text' && (
          <TextComparison {...{ comparisons, j, update, options, variant }} />
        )}
        {compareType === 'confirmation' && (
          <ConfirmationComparison
            {...{ comparisons, j, update, options, variant }}
          />
        )}
        {compareType === 'multiple_choice' && (
          <MultipleChoiceComparison
            {...{ comparisons, j, update, options, variant }}
          />
        )}
        <FlexSpan flex="none" noMargin>
          <Tooltip title="Add comparison">
            <span>
              <SmallIconButton
                disabled={
                  comparisons[j].op === 'ILIKE' && comparisons[j].value === ''
                    ? true
                    : false
                }
                data-testid="logicbuilder-add-comparison"
                onClick={() => {
                  let defaultMap =
                    comparisons.length && comparisons[0].negated
                      ? negatedDefaultComparisonMap[compareType]
                      : defaultComparisonMap[compareType];
                  update({
                    comparisons: [
                      ...comparisons.slice(0, j + 1),
                      {
                        ...defaultMap,
                        _id: generateId()
                      },
                      ...comparisons.slice(j + 1)
                    ]
                  });
                }}
              >
                {variant === 'drawer' ? <AddCircleIcon /> : <AddIcon />}
              </SmallIconButton>
            </span>
          </Tooltip>
          <Tooltip title="Remove comparison">
            <span>
              <SmallIconButton
                data-testid="logicbuilder-remove-comparison"
                onMouseOver={() => setRemovedHovered(true)}
                onMouseLeave={() => setRemovedHovered(false)}
                onClick={() => {
                  if (comparisons.length > 1) {
                    setDeleteMe(true);
                  } else {
                    remove();
                  }
                }}
              >
                {variant === 'drawer' ? <RemoveCircleIcon /> : <RemoveIcon />}
              </SmallIconButton>
            </span>
          </Tooltip>
        </FlexSpan>
      </ComparisonWrapper>
    </Collapse>
  );
};

export const NumberComparison = ({ comparisons, j, update, variant }) => {
  return variant === 'drawer' ? (
    <>
      <FlexSpan flex={3}>
        <Select
          variant="outlined"
          value={comparisons[j].op || '='}
          fullWidth
          data-testid="logicbuilder-number-comparison-select-number-op"
          MenuProps={{ disablePortal: true }}
          onChange={e => {
            update({
              comparisons: dotProp.set(comparisons, `${j}.op`, e.target.value)
            });
          }}
        >
          <MuiMenuItem
            data-testid="logicbuilder-number-comparison-select-number-op-option->"
            value=">"
          >
            more than <LightSpan>(&gt;)</LightSpan>
          </MuiMenuItem>
          <MuiMenuItem
            data-testid="logicbuilder-number-comparison-select-number-op-option->="
            value=">="
          >
            at least <LightSpan>(&ge;)</LightSpan>
          </MuiMenuItem>
          <MuiMenuItem
            data-testid="logicbuilder-number-comparison-select-number-op-option-="
            value="="
          >
            equal to <LightSpan>(=)</LightSpan>
          </MuiMenuItem>
          <MuiMenuItem
            data-testid="logicbuilder-number-comparison-select-number-op-option-<="
            value="<="
          >
            at most <LightSpan>(&le;)</LightSpan>
          </MuiMenuItem>
          <MuiMenuItem
            data-testid="logicbuilder-number-comparison-select-number-op-option-<"
            value="<"
          >
            less than <LightSpan>(&lt;)</LightSpan>
          </MuiMenuItem>
        </Select>
      </FlexSpan>
      <FlexSpan flex={3}>
        <TextField
          type="number"
          variant="outlined"
          placeholder="Value"
          data-testid="logicbuilder-number-comparison-value"
          fullWidth
          value={comparisons[j].value || ''}
          onChange={e => {
            update({
              comparisons: dotProp.set(
                comparisons,
                `${j}.value`,
                e.target.value || null
              )
            });
          }}
        />
      </FlexSpan>
    </>
  ) : variant === 'inline' ? (
    <InlineNumberComparison {...{ comparisons, j, update }} />
  ) : (
    <OutlinedNumberComparison {...{ comparisons, j, update }} />
  );
};

const OutlinedNumberComparison = ({ comparisons, j, update }) => {
  const [inputValue, setInputValue] = useState(comparisons[j].value || '');

  return (
    <FlexSpan flex={6} centerContent={false}>
      <Select
        variant="standard"
        value={comparisons[j].op || '='}
        data-testid="logicbuilder-number-comparison-select-number-op"
        MenuProps={{ disablePortal: true }}
        onChange={e => {
          update({
            comparisons: dotProp.set(comparisons, `${j}.op`, e.target.value)
          });
        }}
      >
        <MuiMenuItem
          data-testid="logicbuilder-number-comparison-select-number-op-option->"
          value=">"
        >
          more than <LightSpan>(&gt;)</LightSpan>
        </MuiMenuItem>
        <MuiMenuItem
          data-testid="logicbuilder-number-comparison-select-number-op-option->="
          value=">="
        >
          at least <LightSpan>(&ge;)</LightSpan>
        </MuiMenuItem>
        <MuiMenuItem
          data-testid="logicbuilder-number-comparison-select-number-op-option-="
          value="="
        >
          equal to <LightSpan>(=)</LightSpan>
        </MuiMenuItem>
        <MuiMenuItem
          data-testid="logicbuilder-number-comparison-select-number-op-option-<="
          value="<="
        >
          at most <LightSpan>(&le;)</LightSpan>
        </MuiMenuItem>
        <MuiMenuItem
          data-testid="logicbuilder-number-comparison-select-number-op-option-<"
          value="<"
        >
          less than <LightSpan>(&lt;)</LightSpan>
        </MuiMenuItem>
      </Select>

      <InputBase
        customCSS={`margin-left: 0.8rem !important; max-width: 200px;`}
        type="number"
        variant="outlined"
        placeholder="Value"
        data-testid="logicbuilder-number-comparison-value"
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        onBlur={e => {
          let value = e.target.value;
          if (value) {
            value = +value;
          }

          update({
            comparisons: dotProp.set(comparisons, `${j}.value`, value || null)
          });
        }}
      />
    </FlexSpan>
  );
};
const InlineNumberComparison = ({ comparisons, j, update }) => {
  const [inputValue, setInputValue] = useState(comparisons[j].value || '');

  return (
    <FlexSpan flex={6} centerContent={false}>
      <Select
        variant="standard"
        value={comparisons[j].op || '='}
        data-testid="logicbuilder-number-comparison-select-number-op"
        MenuProps={{ disablePortal: true }}
        onChange={e => {
          update({
            comparisons: dotProp.set(comparisons, `${j}.op`, e.target.value)
          });
        }}
      >
        <MuiMenuItem
          data-testid="logicbuilder-number-comparison-select-number-op-option->"
          value=">"
        >
          more than <LightSpan>(&gt;)</LightSpan>
        </MuiMenuItem>
        <MuiMenuItem
          data-testid="logicbuilder-number-comparison-select-number-op-option->="
          value=">="
        >
          at least <LightSpan>(&ge;)</LightSpan>
        </MuiMenuItem>
        <MuiMenuItem
          data-testid="logicbuilder-number-comparison-select-number-op-option-="
          value="="
        >
          equal to <LightSpan>(=)</LightSpan>
        </MuiMenuItem>
        <MuiMenuItem
          data-testid="logicbuilder-number-comparison-select-number-op-option-<="
          value="<="
        >
          at most <LightSpan>(&le;)</LightSpan>
        </MuiMenuItem>
        <MuiMenuItem
          data-testid="logicbuilder-number-comparison-select-number-op-option-<"
          value="<"
        >
          less than <LightSpan>(&lt;)</LightSpan>
        </MuiMenuItem>
      </Select>

      <TextField
        style={{ marginLeft: '4px' }}
        type="number"
        variant="standard"
        placeholder="Value"
        data-testid="logicbuilder-number-comparison-value"
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        onBlur={e => {
          let value = e.target.value;
          if (value) {
            value = +value;
          }

          update({
            comparisons: dotProp.set(comparisons, `${j}.value`, value || null)
          });
        }}
      />
    </FlexSpan>
  );
};

export const TextComparison = ({ comparisons, j, update, variant }) => {
  const [string, setString] = React.useState(comparisons[j].value || '');

  const mapOpToValue = {
    ILIKE: 'contains',
    '=': 'blank',
    '<>': 'not-blank'
  };

  const onChange = e => {
    switch (e.target.value) {
      case 'contains':
        return save({ op: 'ILIKE', value: string });
      case 'not-blank':
        return save({ op: '<>', value: '' });
      case 'blank':
        return save({ op: '=', value: '' });
      default:
        return;
    }
  };

  const save = next => {
    update({ comparisons: dotProp.set(comparisons, j, next) });
  };

  const viewProps = {
    comparisons,
    j,

    string,
    setString,
    mapOpToValue,
    onChange,
    save,
    variant
  };

  return variant !== 'drawer' ? (
    <TextComparisonInlineView {...viewProps} />
  ) : (
    <>
      <FlexSpan flex={3}>
        <Select
          variant="outlined"
          value={mapOpToValue[comparisons[j].op] || 'not-blank'}
          fullWidth
          MenuProps={{ disablePortal: true }}
          data-testid="logicbuilder-text-comparison-select-op"
          onChange={onChange}
        >
          <MuiMenuItem
            data-testid="logicbuilder-text-comparison-select-op-option-contains"
            value="contains"
          >
            contains
          </MuiMenuItem>
          <MuiMenuItem
            data-testid="logicbuilder-text-comparison-select-op-option-not-block"
            value="not-blank"
          >
            is not blank
          </MuiMenuItem>
          <MuiMenuItem
            data-testid="logicbuilder-text-comparison-select-op-option-blank"
            value="blank"
          >
            is blank
          </MuiMenuItem>
        </Select>
      </FlexSpan>
      <FlexSpan flex={3}>
        <Collapse in={comparisons[j].op === 'ILIKE'}>
          <TextField
            variant="outlined"
            placeholder="Enter text"
            fullWidth
            data-testid="logicbuilder-text-comparison-value"
            value={unwrapPercents(string) || ''}
            onChange={e => {
              const value = wrapPercents(e.target.value);
              setString(value);

              if (comparisons[j].op === 'ILIKE') {
                save({ op: 'ILIKE', value });
              }
            }}
          />
        </Collapse>
      </FlexSpan>
    </>
  );
};

const TextComparisonInlineView = ({
  comparisons,
  j,
  string,
  setString,
  mapOpToValue,
  onChange,
  save,
  variant
}) => {
  const [inputValue, setInputValue] = useState(unwrapPercents(string) || '');

  return (
    <FlexSpan flex={6} centerContent={false}>
      <Select
        variant="standard"
        value={mapOpToValue[comparisons[j].op] || 'not-blank'}
        MenuProps={{ disablePortal: true }}
        data-testid="logicbuilder-text-comparison-select-op"
        onChange={onChange}
      >
        <MuiMenuItem
          data-testid="logicbuilder-text-comparison-select-op-option-contains"
          value="contains"
        >
          contains
        </MuiMenuItem>
        <MuiMenuItem
          data-testid="logicbuilder-text-comparison-select-op-option-not-block"
          value="not-blank"
        >
          is not blank
        </MuiMenuItem>
        <MuiMenuItem
          data-testid="logicbuilder-text-comparison-select-op-option-blank"
          value="blank"
        >
          is blank
        </MuiMenuItem>
      </Select>
      <Collapse
        in={comparisons[j].op === 'ILIKE'}
        style={{ marginLeft: '4px' }}
      >
        {variant === 'outlined' ? (
          <InputBase
            customCSS={`margin-left: 0.8rem !important; max-width: 200px;`}
            variant="outlined"
            placeholder="Enter text"
            data-testid="logicbuilder-text-comparison-value"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onBlur={e => {
              const value = wrapPercents(e.target.value);
              setString(value);

              if (comparisons[j].op === 'ILIKE') {
                save({ op: 'ILIKE', value });
              }
            }}
          />
        ) : (
          <TextField
            variant="standard"
            placeholder="Enter text"
            data-testid="logicbuilder-text-comparison-value"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onBlur={e => {
              const value = wrapPercents(e.target.value);
              setString(value);

              if (comparisons[j].op === 'ILIKE') {
                save({ op: 'ILIKE', value });
              }
            }}
          />
        )}
      </Collapse>
    </FlexSpan>
  );
};

export const ExactComparison = ({
  comparisons,
  j,
  update,
  options = [],
  variant
}) => {
  const [wrap, setWrap] = useState(true);

  return variant === 'outlined' ? (
    <OutlinedExactComparison {...{ comparisons, j, update, options }} />
  ) : (
    <FlexSpan flex={6} centerContent={variant === 'drawer'}>
      <Select
        variant={variant === 'drawer' ? 'outlined' : 'standard'}
        onOpen={() => setWrap(false)}
        onClose={() => setWrap(true)}
        value={comparisons[j].value || ' '}
        MenuProps={{ disablePortal: true }}
        fullWidth={variant === 'drawer'}
        data-testid="logicbuilder-exact-comparison-select-value"
        onChange={e => {
          const value = e.target.value;
          const op = value === '_FIELD_IS_BLANK' ? 'IS NULL' : '=';
          const comparisonsCpy = SerializationCopy(comparisons);
          comparisonsCpy[j].value = value;
          comparisonsCpy[j].op = op;

          update({
            comparisons: comparisonsCpy
          });
        }}
      >
        <MuiMenuItem disabled value=" " style={{ display: 'none' }}>
          <em>Choose an option</em>
        </MuiMenuItem>
        <MuiMenuItem
          disabled
          value={comparisons[j].value}
          style={{ display: 'none' }}
        >
          <ErrorMessage>
            <ErrorIcon />
            Value <b>{comparisons[j].value}</b> no longer exists
          </ErrorMessage>
        </MuiMenuItem>
        <BlankOption
          data-testid={`logicbuilder-exact-comparison-select-value-blank`}
          value={'_FIELD_IS_BLANK'}
        >
          <TextString content="logic.blank" />
        </BlankOption>
        {options.map((o, i) => (
          <MuiMenuItem
            data-testid={`logicbuilder-exact-comparison-select-value-option-${i}`}
            value={o}
            key={o}
          >
            <Wrap>
              <Typography noWrap={wrap}>{o}</Typography>
            </Wrap>
          </MuiMenuItem>
        ))}
      </Select>
    </FlexSpan>
  );
};
export const OutlinedExactComparison = ({
  comparisons,
  j,
  update,
  options = []
}) => {
  return (
    <FlexSpan flex={6}>
      <OTSelect
        customCSS={`margin-right: auto !important;`}
        variant="outlined"
        value={comparisons[j].value || ' '}
        data-testid="logicbuilder-exact-comparison-select-value"
        onChange={v => {
          const op = v === '_FIELD_IS_BLANK' ? 'IS NULL' : '=';
          const comparisonsCpy = SerializationCopy(comparisons);
          comparisonsCpy[j].value = v;
          comparisonsCpy[j].op = op;

          update({
            comparisons: comparisonsCpy
          });
        }}
        placeholder={
          options.find(o => o === comparisons[j].value || ' ')
            ? 'Choose an option'
            : 'Option no longer exist!'
        }
        options={options.map(o => ({
          value: o,
          text: o,
          'data-testid': `logicbuilder-exact-comparison-select-value-option-${o}`
        }))}
      />

      {/* <MuiMenuItem
          disabled
          value={comparisons[j].value}
          style={{ display: 'none' }}
        >
          <ErrorMessage>
            <ErrorIcon />
            Value <b>{comparisons[j].value}</b> no longer exists
          </ErrorMessage>
        </MuiMenuItem> */}
    </FlexSpan>
  );
};

export const MultipleChoiceComparison = ({
  comparisons,
  j,
  update,
  options = [],
  variant
}) => {
  const [wrap, setWrap] = useState(true);
  let optionPairs = options.map(one => {
    return {
      text: one.label ? one.label : one,
      value: one.internalId ? one.internalId : one
    };
  });
  return variant === 'outlined' ? (
    <OutlinedMultipleChoiceComparison
      {...{ comparisons, j, update, options }}
    />
  ) : (
    <FlexSpan flex={6} centerContent={variant === 'drawer'}>
      <Select
        fullWidth={variant === 'drawer'}
        onOpen={() => setWrap(false)}
        onClose={() => setWrap(true)}
        variant={variant === 'drawer' ? 'outlined' : 'standard'}
        value={comparisons[j].value || ' '}
        MenuProps={{ disablePortal: true }}
        data-testid="logicbuilder-multiple-choice-comparison-select-value"
        onChange={e => {
          update({
            comparisons: dotProp.set(comparisons, `${j}.value`, e.target.value)
          });
        }}
      >
        <MuiMenuItem disabled value=" " style={{ display: 'none' }}>
          <em>Choose an option</em>
        </MuiMenuItem>
        <MuiMenuItem
          disabled
          value={comparisons[j].value}
          style={{ display: 'none' }}
        >
          <ErrorMessage>
            <ErrorIcon />
            Value{' '}
            <b>
              {comparisons[j].value &&
                comparisons[j].value.replace('%||', '').replace('||%', '')}
            </b>{' '}
            no longer exists
          </ErrorMessage>
        </MuiMenuItem>
        <BlankOption
          data-testid={`logicbuilder-multiple-choice-comparison-select-value-blank`}
          value="||||"
        >
          <TextString content="logic.blank" />
        </BlankOption>
        {optionPairs.map((o, i) => {
          const v = wrapPercents(`||${o.value}||`);
          return (
            <MuiMenuItem
              data-testid={`logicbuilder-multiple-choice-comparison-select-value-option-${i}`}
              value={v}
              key={v}
            >
              <Wrap>
                <Typography noWrap={wrap}>{o.text}</Typography>
              </Wrap>
            </MuiMenuItem>
          );
        })}
      </Select>
    </FlexSpan>
  );
};
export const OutlinedMultipleChoiceComparison = ({
  comparisons,
  j,
  update,
  options = [],
  variant
}) => {
  return (
    <FlexSpan flex={6}>
      <OTSelect
        customCSS={`margin-right: auto !important;`}
        variant="outlined"
        value={comparisons[j].value || ' '}
        data-testid="logicbuilder-multiple-choice-comparison-select-value"
        onChange={v => {
          update({
            comparisons: dotProp.set(comparisons, `${j}.value`, v)
          });
        }}
        placeholder={
          options.find(o => o === comparisons[j].value) ||
          comparisons[j].value === null
            ? 'Choose an option'
            : 'Option no longer exist!'
        }
        options={options.map(o => ({
          value: wrapPercents(`||${o}||`),
          text: o,
          'data-testid': `logicbuilder-multiple-choice-comparison-select-value-option-${o}`
        }))}
      />
    </FlexSpan>
  );
};

export const YesNoComparison = ({ comparisons, j, update, variant }) => {
  return variant === 'outlined' ? (
    <OutlinedYesNoComparison {...{ comparisons, j, update }} />
  ) : (
    <FlexSpan flex={6} centerContent={variant === 'drawer'}>
      <Select
        variant={variant === 'drawer' ? 'outlined' : 'standard'}
        value={comparisons[j].value || ''}
        MenuProps={{ disablePortal: true }}
        fullWidth={variant === 'drawer'}
        data-testid="logicbuilder-yes_no-comparison-select-value"
        onChange={e => {
          update({
            comparisons: dotProp.set(comparisons, `${j}.value`, e.target.value)
          });
        }}
      >
        <MuiMenuItem disabled value="" style={{ display: 'none' }}>
          <em>Choose an option</em>
        </MuiMenuItem>
        <BlankOption
          data-testid={`logicbuilder-yes_no-comparison-select-value-blank`}
          value={'_FIELD_IS_BLANK'}
        >
          <TextString content="logic.blank" />
        </BlankOption>
        {['Yes', 'No'].map(o => (
          <MuiMenuItem
            value={o}
            key={o}
            data-testid={`logicbuilder-yes_no-comparison-select-value-option-${o}`}
          >
            <Wrap>
              <Typography noWrap={true}>{o}</Typography>
            </Wrap>
          </MuiMenuItem>
        ))}
      </Select>
    </FlexSpan>
  );
};

export const OutlinedYesNoComparison = ({
  comparisons,
  j,
  update,
  variant
}) => {
  return (
    <FlexSpan flex={6}>
      <OTSelect
        customCSS={`margin-right: auto !important;`}
        variant="outlined"
        value={comparisons[j].value || ''}
        data-testid="logicbuilder-yes_no-comparison-select-value"
        onChange={v => {
          update({
            comparisons: dotProp.set(comparisons, `${j}.value`, v)
          });
        }}
        placeholder="Choose an option"
        options={[
          {
            value: 'Yes',
            text: 'Yes',
            'data-testid':
              'logicbuilder-yes_no-comparison-select-value-option-Yes'
          },
          {
            value: 'No',
            text: 'No',
            'data-testid':
              'logicbuilder-yes_no-comparison-select-value-option-No'
          }
        ]}
      />
    </FlexSpan>
  );
};
export const ConfirmationComparison = ({ comparisons, j, update, variant }) => {
  return variant === 'outlined' ? (
    <OutlinedConfirmationComparison {...{ comparisons, j, update }} />
  ) : (
    <FlexSpan flex={6} centerContent={variant === 'drawer'}>
      <Select
        variant={variant === 'drawer' ? 'outlined' : 'standard'}
        value={comparisons[j].value || 'false'}
        MenuProps={{ disablePortal: true }}
        fullWidth={variant === 'drawer'}
        data-testid="logicbuilder-confirmation-comparison-select-value"
        onChange={e => {
          const value = e.target.value;
          const updatedComparisons = JSON.parse(JSON.stringify(comparisons));
          updatedComparisons[j].op = value === 'false' ? 'IS NULL' : '=';
          updatedComparisons[j].value = value === 'false' ? undefined : 'true';
          update({
            comparisons: updatedComparisons
          });
        }}
      >
        <MuiMenuItem disabled value="" style={{ display: 'none' }}>
          <em>Choose an option</em>
        </MuiMenuItem>
        {[
          { t: 'True', v: 'true' },
          { t: 'False', v: 'false' }
        ].map(o => (
          <MuiMenuItem
            value={o.v}
            key={o.t}
            data-testid={`logicbuilder-confirmation-comparison-select-value-option-${o.t}`}
          >
            <Wrap>
              <Typography noWrap={true}>{o.t}</Typography>
            </Wrap>
          </MuiMenuItem>
        ))}
      </Select>
    </FlexSpan>
  );
};
export const OutlinedConfirmationComparison = ({
  comparisons,
  j,
  update,
  variant
}) => {
  return (
    <FlexSpan flex={6}>
      <OTSelect
        customCSS={`margin-right: auto !important;`}
        variant="outlined"
        value={comparisons[j].value || 'false'}
        data-testid="logicbuilder-confirmation-comparison-select-value"
        onChange={value => {
          const updatedComparisons = JSON.parse(JSON.stringify(comparisons));
          updatedComparisons[j].op = value === 'false' ? 'IS NULL' : '=';
          updatedComparisons[j].value = value === 'false' ? undefined : 'true';
          update({
            comparisons: updatedComparisons
          });
        }}
        placeholder="Choose an option"
        options={[
          {
            text: 'True',
            value: 'true',
            'data-testid':
              'logicbuilder-confirmation-comparison-select-value-option-True'
          },
          {
            text: 'False',
            value: 'false',
            'data-testid':
              'logicbuilder-confirmation-comparison-select-value-option-False'
          }
        ]}
      />
    </FlexSpan>
  );
};

export const DropdownComparison = ({
  comparisons,
  j,
  update,
  options = [],
  variant,
  negate = v => v
}) => {
  const [wrap, setWrap] = useState(true);
  const [string, setString] = React.useState(comparisons[j].value || '');

  const save = next => {
    update({ comparisons: dotProp.set(comparisons, j, next) });
  };

  const mapOpToValue = {
    ILIKE: 'contains',
    '=': 'is',
    '<>': 'is_not',
    'IS NULL': 'is',
    'IS NOT NULL': 'is_not'
  };

  const getOPBasedOnVal = (op, val) => {
    if (op === 'is' && val !== '_FIELD_IS_BLANK') {
      return '=';
    }
    if (op === 'is_not' && val !== '_FIELD_IS_BLANK') {
      return '<>';
    }
    if (op === 'is' && val === '_FIELD_IS_BLANK') {
      return 'IS NULL';
    }
    if (op === 'is_not' && val === '_FIELD_IS_BLANK') {
      return 'IS NOT NULL';
    }
    return op;
  };

  const onChange = e => {
    const isOpIsContains = comparisons[j].op === 'ILIKE';

    switch (e.target.value) {
      case 'contains':
        return save({ op: 'ILIKE', value: string });
      case 'is':
        return save({
          op: getOPBasedOnVal('is', comparisons[j].value),
          value: isOpIsContains ? '' : comparisons[j].value,
          negated: false
        });
      case 'is_not':
        return save({
          op: getOPBasedOnVal('is_not', comparisons[j].value),
          value: isOpIsContains ? '' : comparisons[j].value,
          negated: comparisons[j].value === '_FIELD_IS_BLANK'
        });
      default:
        return;
    }
  };

  return (
    <>
      <FlexSpan flex={3} centerContent={variant === 'drawer'}>
        {/* <span> */}
        <Select
          fullWidth
          variant="outlined"
          value={mapOpToValue[comparisons[j].op] || 'is'}
          MenuProps={{ disablePortal: true }}
          data-testid="logicbuilder-text-comparison-select-op"
          onChange={onChange}
        >
          <MuiMenuItem
            data-testid="logicbuilder-text-comparison-select-op-option-is"
            value="is"
          >
            is
          </MuiMenuItem>
          <MuiMenuItem
            data-testid="logicbuilder-text-comparison-select-op-option-is_not"
            value="is_not"
          >
            is not
          </MuiMenuItem>

          <MuiMenuItem
            data-testid="logicbuilder-text-comparison-select-op-option-contains"
            value="contains"
          >
            contains
          </MuiMenuItem>
        </Select>
      </FlexSpan>
      <FlexSpan flex={3} centerContent={variant === 'drawer'}>
        {/* <Animation
          unmountOnExit
          mountOnEnter
          type="collapse"
          in={mapOpToValue[comparisons[j].op] !== 'contains'}
        > */}
        <Collapse
          in={mapOpToValue[comparisons[j].op] !== 'contains'}
          unmountOnExit
          mountOnEnter
        >
          <Select
            style={{ minWidth: 210, maxHeight: 56 }}
            variant={variant === 'drawer' ? 'outlined' : 'standard'}
            onOpen={() => setWrap(false)}
            onClose={() => setWrap(true)}
            value={comparisons[j].value || ' '}
            MenuProps={{ disablePortal: true }}
            fullWidth
            data-testid="logicbuilder-exact-comparison-select-value"
            onChange={e => {
              const value = e.target.value;

              let op = comparisons[j].op;
              const valueIsBlank = value === '_FIELD_IS_BLANK';

              if (
                valueIsBlank &&
                mapOpToValue[comparisons[j].op] === 'is_not'
              ) {
                op = 'IS NOT NULL';
              }
              if (valueIsBlank && mapOpToValue[comparisons[j].op] === 'is') {
                op = 'IS NULL';
              }

              const comparisonsCpy = SerializationCopy(comparisons);
              comparisonsCpy[j].value = value;
              comparisonsCpy[j].op = op;

              update({
                comparisons: comparisonsCpy
              });
            }}
          >
            <MuiMenuItem disabled value=" " style={{ display: 'none' }}>
              <em>Choose an option</em>
            </MuiMenuItem>
            <MuiMenuItem
              disabled
              value={comparisons[j].value}
              style={{ display: 'none' }}
            >
              <ErrorMessage>
                <ErrorIcon />
                Value <b>{comparisons[j].value}</b> no longer exists
              </ErrorMessage>
            </MuiMenuItem>
            <BlankOption
              data-testid={`logicbuilder-exact-comparison-select-value-blank`}
              value={'_FIELD_IS_BLANK'}
            >
              <TextString content="logic.blank" />
            </BlankOption>
            {options.map((o, i) => (
              <MuiMenuItem
                data-testid={`logicbuilder-exact-comparison-select-value-option-${i}`}
                value={o}
                key={o}
              >
                <Wrap>
                  <Typography noWrap={wrap}>{o}</Typography>
                </Wrap>
              </MuiMenuItem>
            ))}
          </Select>
        </Collapse>
        {/* </Animation>
        {/* <Animation
          unmountOnExit
          mountOnEnter
          type="collapse"
          in={mapOpToValue[comparisons[j].op] === 'contains'}
        > */}
        <Collapse in={comparisons[j].op === 'ILIKE'} unmountOnExit mountOnEnter>
          <TextField
            variant="outlined"
            placeholder="Enter text"
            fullWidth
            data-testid="logicbuilder-text-comparison-value"
            value={unwrapPercents(string) || ''}
            onChange={e => {
              const value = wrapPercents(e.target.value);
              setString(value);

              if (comparisons[j].op === 'ILIKE') {
                save({ op: 'ILIKE', value });
              }
            }}
          />
        </Collapse>
        {/* </span> */}
      </FlexSpan>
    </>
  );
};
